import { useEffect, useMemo, useRef } from 'react'

type SessionWithCurrent = {
  isCurrent?: boolean
}

type Params<TSession extends SessionWithCurrent> = {
  isAuthenticated: boolean
  section: string
  sessions: TSession[]
  sessionsLoading: boolean
  loadSessions: () => Promise<void>
  teamMembers: unknown[]
  roleDefinitions: unknown[]
  teamLoading: boolean
  loadTeam: () => Promise<void>
}

export const useAdminSectionLifecycle = <TSession extends SessionWithCurrent>({
  isAuthenticated,
  section,
  sessions,
  sessionsLoading,
  loadSessions,
  teamMembers,
  roleDefinitions,
  teamLoading,
  loadTeam,
}: Params<TSession>) => {
  const teamAutoLoadAttemptedRef = useRef(false)
  const currentSession = useMemo(
    () => (sessions.length ? sessions.find((session) => session.isCurrent) ?? sessions[0] : null),
    [sessions],
  )

  useEffect(() => {
    if (!isAuthenticated) return
    if (section !== 'profile-security' && section !== 'profile-sessions') return
    if (sessionsLoading || sessions.length > 0) return
    void loadSessions()
  }, [isAuthenticated, loadSessions, section, sessions.length, sessionsLoading])

  useEffect(() => {
    if (!isAuthenticated) return
    if (section !== 'profile-team' && section !== 'profile-team-edit') {
      teamAutoLoadAttemptedRef.current = false
      return
    }
    if (teamLoading) return
    if (teamMembers.length > 0 || roleDefinitions.length > 0) {
      teamAutoLoadAttemptedRef.current = false
      return
    }
    if (teamAutoLoadAttemptedRef.current) return
    teamAutoLoadAttemptedRef.current = true
    void loadTeam()
  }, [isAuthenticated, loadTeam, roleDefinitions.length, section, teamLoading, teamMembers.length])

  return { currentSession }
}
